import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, User as UserIcon, ArrowLeft } from "lucide-react";
import { Logo } from "../components/Logo";
import { api, formatError } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function AuthPage({ mode }) {
  const nav = useNavigate();
  const { setUser, setData } = useApp();
  const [email, setEmail] = useState(mode === "login" ? "demo@localboost.ai" : "");
  const [password, setPassword] = useState(mode === "login" ? "localboost123" : "");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const isLogin = mode === "login", isRegister = mode === "register", isForgot = mode === "forgot";

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isForgot) {
        await api.post("/auth/forgot-password", { email });
        setSent(true);
        toast.success("Si le compte existe, un lien a été envoyé (voir logs backend).");
      } else if (isLogin) {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data); if (data.profile) setData(data.profile);
        toast.success("Bienvenue !");
        nav(data.profile ? "/app/dashboard" : "/onboarding");
      } else {
        const { data } = await api.post("/auth/register", { email, password, name: name || "Entrepreneur" });
        setUser(data);
        toast.success("Compte créé !");
        nav("/onboarding");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 relative overflow-hidden grain">
      <div className="absolute inset-0 aurora" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md">
        <button onClick={() => nav("/")} className="inline-flex items-center gap-1.5 min-h-[44px] sm:min-h-0 -ml-1 px-1 sm:ml-0 sm:px-0 text-sm text-[hsl(var(--muted-foreground))] hover:text-white mb-4 sm:mb-6" data-testid="back-home">
          <ArrowLeft size={16} /> Accueil
        </button>
        <div className="lb-card p-8">
          <div className="mb-6"><Logo /></div>
          <h1 className="font-display font-bold text-2xl mb-1">
            {isLogin ? "Connexion" : isRegister ? "Créer un compte" : "Mot de passe oublié"}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
            {isForgot ? "Recevez un lien de réinitialisation." : "Votre copilote commercial vous attend."}
          </p>

          {sent ? (
            <div className="text-center py-6">
              <p className="text-emerald-400 mb-4">Lien envoyé. Vérifiez les logs backend.</p>
              <Button onClick={() => nav("/login")} className="rounded-full" data-testid="back-login">Retour connexion</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {isRegister && (
                <div>
                  <Label className="text-xs">Nom</Label>
                  <div className="relative mt-1.5">
                    <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                    <Input data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom" className="pl-9 bg-white/5 border-white/10 h-11" />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs">Email</Label>
                <div className="relative mt-1.5">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                  <Input data-testid="input-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@commerce.fr" className="pl-9 bg-white/5 border-white/10 h-11" />
                </div>
              </div>
              {!isForgot && (
                <div>
                  <Label className="text-xs">Mot de passe</Label>
                  <div className="relative mt-1.5">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                    <Input data-testid="input-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9 bg-white/5 border-white/10 h-11" />
                  </div>
                </div>
              )}
              <Button data-testid="auth-submit" type="submit" disabled={loading}
                className="w-full rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
                {loading ? "..." : isLogin ? "Se connecter" : isRegister ? "Créer mon compte" : "Envoyer le lien"}
              </Button>
            </form>
          )}

          {!isForgot && !sent && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-[hsl(var(--muted-foreground))]">ou</span><div className="flex-1 h-px bg-white/10" />
              </div>
              <Button data-testid="google-login" onClick={googleLogin} variant="outline" className="w-full rounded-full h-11 border-white/10 bg-white/5 hover:bg-white/10">
                <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                Continuer avec Google
              </Button>
            </>
          )}

          <div className="mt-6 text-center text-sm text-[hsl(var(--muted-foreground))] space-y-1">
            {isLogin && <><p><button onClick={() => nav("/register")} className="inline-flex items-center justify-center min-h-[44px] sm:min-h-0 text-neon hover:underline" data-testid="to-register">Créer un compte</button></p><p><button onClick={() => nav("/forgot")} className="inline-flex items-center justify-center min-h-[44px] sm:min-h-0 hover:text-white" data-testid="to-forgot">Mot de passe oublié ?</button></p></>}
            {isRegister && <p>Déjà inscrit ? <button onClick={() => nav("/login")} className="inline-flex items-center justify-center min-h-[44px] sm:min-h-0 text-neon hover:underline" data-testid="to-login">Se connecter</button></p>}
            {isForgot && <p><button onClick={() => nav("/login")} className="inline-flex items-center justify-center min-h-[44px] sm:min-h-0 text-neon hover:underline">Retour connexion</button></p>}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
